/**
 * Server state of customization: card covers and looks, column and board appearance,
 * link previews and moving cards/columns between boards.
 *
 * Card mutations return the whole card; it replaces the card detail cache and the board
 * preview. Column/board looks are small objects patched into the board detail directly.
 */
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';

import type { BoardAppearance, CoverInput, Look } from '@/entities/appearance';
import { api, apiUpload, queryKeys } from '@/shared/api';

import { invalidateBoard, syncCardInBoard, updateDetail } from './api';
import type { Board, Card, Column, ColumnAppearance, Label, LinkPreview } from './types';

type CardDetail = Card & { content_blocks: unknown[] };

/** Put a fresh server copy of a card everywhere it is cached. */
export function applyServerCard(client: QueryClient, card: Card): void {
  client.setQueryData<CardDetail>(queryKeys.cards.detail(card.id), (current) =>
    current ? { ...current, ...card } : current,
  );
  syncCardInBoard(client, card);
}

// --- card cover and look ------------------------------------------------------

export function useSetCardCover() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, cover }: { cardId: string; cover: CoverInput }) =>
      api.put<Card>(`/v1/cards/${cardId}/cover`, cover),
    onSuccess: (card) => {
      applyServerCard(client, card);
    },
  });
}

/** Removes only the cover; an attachment that served as one stays. */
export function useClearCardCover() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (cardId: string) => api.delete<Card>(`/v1/cards/${cardId}/cover`),
    onSuccess: (card) => {
      applyServerCard(client, card);
    },
  });
}

/** The card's own look; `null` goes back to its column's default ("use the column look"). */
export function useSetCardLook() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, look }: { cardId: string; look: Look | null }) =>
      look
        ? api.put<Card>(`/v1/cards/${cardId}/appearance`, look)
        : api.delete<Card>(`/v1/cards/${cardId}/appearance`),
    onSuccess: (card) => {
      applyServerCard(client, card);
    },
  });
}

// --- link preview ----------------------------------------------------------------

function patchPreview(client: QueryClient, card: Card, preview: LinkPreview): void {
  client.setQueryData<CardDetail>(queryKeys.cards.detail(card.id), (current) =>
    current ? { ...current, link_preview: preview } : current,
  );
  updateDetail(client, card.placement.board_id, (detail) => ({
    ...detail,
    cards: detail.cards.map((c) => (c.id === card.id ? { ...c, link_preview: preview } : c)),
  }));
}

/** Re-fetch the site's metadata (asynchronously on the server). The card title is untouched. */
export function useRefreshLinkPreview() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (card: Card) => api.post<LinkPreview>(`/v1/cards/${card.id}/link-preview/refresh`),
    onSuccess: (preview, card) => {
      patchPreview(client, card, preview);
    },
  });
}

// --- moving between boards -------------------------------------------------------------

export interface CardMoved {
  card: Card;
  removed_labels: Pick<Label, 'id' | 'name' | 'color'>[];
}

/** Move the same card to a column of another board; the server puts it at the end. */
export function useMoveCardToBoard(sourceBoardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      cardId,
      boardId,
      columnId,
    }: {
      cardId: string;
      boardId: string;
      columnId: string;
    }) =>
      api.post<CardMoved>(`/v1/cards/${cardId}/move-to-board`, {
        board_id: boardId,
        column_id: columnId,
      }),
    onSuccess: (moved, { boardId }) => {
      updateDetail(client, sourceBoardId, (detail) => ({
        ...detail,
        cards: detail.cards.filter((c) => c.id !== moved.card.id),
      }));
      void invalidateBoard(client, sourceBoardId);
      void invalidateBoard(client, boardId);
      void client.invalidateQueries({ queryKey: queryKeys.cards.detail(moved.card.id) });
    },
  });
}

export interface ColumnMoved {
  column: Column;
  active_cards: number;
  archived_cards: number;
  trashed_cards: number;
  removed_labels: number;
}

/** Move the column with all its cards to the end of another board. */
export function useMoveColumnToBoard(sourceBoardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ columnId, boardId }: { columnId: string; boardId: string }) =>
      api.post<ColumnMoved>(`/v1/columns/${columnId}/move-to-board`, { board_id: boardId }),
    onSuccess: (_moved, { columnId, boardId }) => {
      updateDetail(client, sourceBoardId, (detail) => ({
        ...detail,
        columns: detail.columns.filter((c) => c.id !== columnId),
        cards: detail.cards.filter((c) => c.placement.column_id !== columnId),
      }));
      void invalidateBoard(client, sourceBoardId);
      void invalidateBoard(client, boardId);
      void client.invalidateQueries({ queryKey: queryKeys.cards.all });
    },
  });
}

// --- column appearance ------------------------------------------------------------------

function replaceColumn(client: QueryClient, boardId: string, column: Column): void {
  updateDetail(client, boardId, (detail) => ({
    ...detail,
    columns: detail.columns.map((c) => (c.id === column.id ? column : c)),
  }));
}

export interface ColumnLookInput {
  color: string | null;
  appearance_mode: ColumnAppearance;
  extra: Column['appearance'];
}

export function useSetColumnAppearance(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ columnId, ...look }: { columnId: string } & ColumnLookInput) =>
      api.put<Column>(`/v1/columns/${columnId}/appearance`, look),
    onSuccess: (column) => {
      replaceColumn(client, boardId, column);
    },
  });
}

/** The default look of the column's cards; `null` = standard cards. */
export function useSetColumnCardLook(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ columnId, look }: { columnId: string; look: Look | null }) =>
      look
        ? api.put<Column>(`/v1/columns/${columnId}/card-look`, look)
        : api.delete<Column>(`/v1/columns/${columnId}/card-look`),
    onSuccess: (column) => {
      replaceColumn(client, boardId, column);
    },
  });
}

export function useColumnBackgroundImage(boardId: string) {
  const client = useQueryClient();
  return {
    upload: useMutation({
      mutationFn: ({ columnId, file }: { columnId: string; file: File }) =>
        apiUpload<Column>(`/v1/columns/${columnId}/background-image`, file, {
          filename: file.name || 'background',
          method: 'PUT',
        }),
      onSuccess: (column) => {
        replaceColumn(client, boardId, column);
      },
    }),
    remove: useMutation({
      mutationFn: (columnId: string) =>
        api.delete<Column>(`/v1/columns/${columnId}/background-image`),
      onSuccess: (column) => {
        replaceColumn(client, boardId, column);
      },
    }),
  };
}

// --- board appearance ---------------------------------------------------------------------

function replaceBoard(client: QueryClient, board: Board): void {
  updateDetail(client, board.id, (detail) => ({ ...detail, board }));
  if (board.workspace_id) {
    void client.invalidateQueries({ queryKey: queryKeys.boards.list(board.workspace_id) });
  }
  // the Home catalog card shows the tile look too
  void client.invalidateQueries({ queryKey: queryKeys.collab.myBoards });
  void client.invalidateQueries({ queryKey: queryKeys.calendar.all });
}

export function useSetBoardAppearance(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (appearance: BoardAppearance) =>
      api.put<Board>(`/v1/boards/${boardId}/appearance`, appearance),
    onSuccess: (board) => {
      replaceBoard(client, board);
    },
  });
}

export function useBoardBackgroundImage(boardId: string) {
  const client = useQueryClient();
  return {
    upload: useMutation({
      mutationFn: (file: File) =>
        apiUpload<Board>(`/v1/boards/${boardId}/background-image`, file, {
          filename: file.name || 'background',
          method: 'PUT',
        }),
      onSuccess: (board) => {
        replaceBoard(client, board);
      },
    }),
    remove: useMutation({
      mutationFn: () => api.delete<Board>(`/v1/boards/${boardId}/background-image`),
      onSuccess: (board) => {
        replaceBoard(client, board);
      },
    }),
  };
}
